import { defineStore } from "pinia";
import { ref } from "vue";
import { api, cancelActiveRequests, getToken, isAuthenticationError, setToken } from "../api";
import { clearAttachmentImageCache } from "../attachmentImageCache";
import { readableError } from "../composables/format";
import { clearOfflineSnapshots } from "../offlineSnapshot";
import type { User, UserSettingsPatch } from "../types";

export const useAuthStore = defineStore("auth", () => {
  const USER_KEY = "mindback.authUser";
  const user = ref<User | null>(null);
  const notice = ref("");
  const booted = ref(false);
  const offline = ref(false);
  let authGeneration = 0;

  async function boot() {
    if (booted.value) return;
    booted.value = true;
    if (!getToken()) return;
    const generation = authGeneration;
    try {
      const response = await api.me();
      if (generation !== authGeneration) return;
      user.value = response.user;
      persistUser(response.user);
      notice.value = "";
      offline.value = false;
    } catch (error) {
      if (generation !== authGeneration) return;
      if (isAuthenticationError(error)) {
        clearSession();
        notice.value = readableError(error);
      } else {
        const cached = cachedUser();
        if (cached) {
          user.value = cached;
          offline.value = true;
          notice.value = "网络不可用，已进入离线只读模式";
        } else {
          notice.value = readableError(error);
        }
      }
    }
  }

  async function login(email: string, password: string) {
    authGeneration += 1;
    cancelActiveRequests();
    const response = await api.login(email, password);
    clearAttachmentImageCache();
    setToken(response.token);
    user.value = response.user;
    persistUser(response.user);
    notice.value = "";
    offline.value = false;
    return response.user;
  }

  async function register(email: string, password: string) {
    authGeneration += 1;
    cancelActiveRequests();
    const response = await api.register(email, password);
    clearAttachmentImageCache();
    setToken(response.token);
    user.value = response.user;
    persistUser(response.user);
    notice.value = "";
    offline.value = false;
    return response.user;
  }

  function logout() {
    authGeneration += 1;
    cancelActiveRequests();
    clearSession();
  }

  async function updateSettings(payload: UserSettingsPatch) {
    const response = await api.settings(payload);
    user.value = response.user;
    persistUser(response.user);
    offline.value = false;
    return response.user;
  }

  function cachedUser() {
    try {
      const value = localStorage.getItem(USER_KEY);
      return value ? JSON.parse(value) as User : null;
    } catch {
      return null;
    }
  }

  function persistUser(nextUser: User) {
    localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
  }

  function clearSession() {
    clearAttachmentImageCache();
    if (user.value?.id) clearOfflineSnapshots(user.value.id).catch(() => undefined);
    setToken(null);
    localStorage.removeItem(USER_KEY);
    user.value = null;
    offline.value = false;
    notice.value = "";
  }

  return {
    user,
    offline,
    notice,
    boot,
    login,
    register,
    logout,
    updateSettings
  };
});
